import { ApiClient } from '@/lib/axios/client';
import type { Report, ReportAccess, ReportAccessList, ReportListing } from './types';

export class ReportsApi {
  /**
   * Get all reports for the current organization
   */
  static async getReports(orgId: string): Promise<ReportListing> {
    return await ApiClient.get<ReportListing>('/api/v1/reports');
  }

  /**
   * Get report access list
   */
  static async getReportAccess(reportId: string): Promise<ReportAccessList> {
    return await ApiClient.get<ReportAccessList>(
      `/api/v1/reports/${reportId}/access`
    );
  }

  /**
   * Share report with team members
   */
  static async shareReport(reportId: string, access: ReportAccess[]): Promise<ReportAccess[]> {
    return await ApiClient.post<ReportAccess[]>(
      `/api/v1/reports/${reportId}/access`,
      { users: access }
    );
  }

  /**
   * Revoke report access from a user
   */
  static async revokeAccess(reportId: string, access: ReportAccess[]): Promise<void> {
    await ApiClient.patch(`/api/v1/reports/${reportId}/access`, { users: access });
  }

  /**
   * Create new report
   */
  static async createReport(
    orgId: string,
    reportData: Omit<Report, 'id' | 'org_id' | 'created_at' | 'updated_at'>
  ): Promise<Report> {
    return await ApiClient.post<Report>('/api/v1/reports', {
      ...reportData,
      org_id: orgId,
    });
  }

  /**
   * Update existing report
   */
  static async updateReport(
    reportId: string,
    reportData: Partial<Omit<Report, 'id' | 'org_id' | 'created_at'>>
  ): Promise<Report> {
    return await ApiClient.patch<Report>(
      `/api/v1/reports/${reportId}`,
      reportData
    );
  }

  /**
   * Delete report
   */
  static async deleteReport(reportId: string): Promise<void> {
    await ApiClient.delete(`/api/v1/reports/${reportId}`);
  }
}